import { useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try WhatsApp or call us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    background: "var(--goat-cream)",
    border: "2.5px solid var(--goat-green)",
    color: "var(--goat-green)",
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="contact-page">
      <h1 className="text-5xl md:text-6xl uppercase tracking-tighter" style={{ color: "var(--goat-green)" }} data-testid="contact-heading">
        Contact Us
      </h1>
      <p className="mt-4 text-lg md:text-xl max-w-xl" style={{ color: "var(--goat-green)" }} data-testid="contact-subtext">
        Questions, bulk orders, party crates — reach out and we'll sort you out.
      </p>

      <div className="mt-10 sticker-card sticker-card-orange p-8" data-testid="contact-party-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl uppercase" style={{ color: "var(--goat-green)" }}>Party Crates</h2>
            <p className="mt-2 text-lg max-w-xl" style={{ color: "var(--goat-green)" }}>
              Office lunches, birthdays, events — slider crates built for your crowd. Tell us your headcount and budget, we'll do the rest.
            </p>
          </div>
          <a
            href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GOAT Sliders! I'd like to plan a Party Crate. Headcount: ")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="sticker-btn shrink-0 px-7 py-4 font-display uppercase text-base"
            style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }}
            data-testid="contact-party-btn"
            aria-label="Plan a party crate on WhatsApp"
          >
            Plan My Crate
          </a>
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3 sticker-card p-8" data-testid="contact-form-card">
          <h2 className="text-3xl uppercase mb-6" style={{ color: "var(--goat-green)" }}>Send a message</h2>
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div>
              <label className="block text-sm uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--goat-green)" }} htmlFor="contact-name">Name</label>
              <input id="contact-name" required value={form.name} onChange={update("name")} className="w-full px-4 py-3 rounded-xl text-base outline-none" style={inputStyle} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--goat-green)" }} htmlFor="contact-phone">Phone</label>
              <input id="contact-phone" required type="tel" value={form.phone} onChange={update("phone")} className="w-full px-4 py-3 rounded-xl text-base outline-none" style={inputStyle} data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--goat-green)" }} htmlFor="contact-email">Email (optional)</label>
              <input id="contact-email" type="email" value={form.email} onChange={update("email")} className="w-full px-4 py-3 rounded-xl text-base outline-none" style={inputStyle} data-testid="contact-email-input" />
            </div>
            <div>
              <label className="block text-sm uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--goat-green)" }} htmlFor="contact-message">Message</label>
              <textarea id="contact-message" required rows={4} value={form.message} onChange={update("message")} className="w-full px-4 py-3 rounded-xl text-base outline-none resize-none" style={inputStyle} data-testid="contact-message-input" />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="sticker-btn px-7 py-4 font-display uppercase text-base flex items-center gap-2 disabled:opacity-60"
              style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }}
              data-testid="contact-submit-btn"
            >
              <Send size={18} /> {sending ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>

        <div className="md:col-span-2 space-y-6">
          <div className="sticker-card sticker-card-orange p-8" style={{ background: "var(--goat-green)" }} data-testid="contact-quick-card">
            <h2 className="text-3xl uppercase mb-4" style={{ color: "var(--goat-cream)" }}>Quick reach</h2>
            <p className="text-base mb-6" style={{ color: "var(--goat-cream)" }}>
              Fastest way to us — WhatsApp or a quick call.
            </p>
            <div className="flex flex-col gap-4">
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GOAT Sliders! I'd like to enquire.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="sticker-btn px-6 py-4 flex items-center gap-3 font-semibold uppercase text-sm"
                style={{ background: "var(--goat-orange)", color: "var(--goat-white)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
                data-testid="contact-whatsapp-btn"
                aria-label="Chat on WhatsApp"
              >
                <MessageCircle size={20} /> {CONTACT.whatsappDisplay}
              </a>
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                className="sticker-btn px-6 py-4 flex items-center gap-3 font-semibold uppercase text-sm"
                style={{ background: "var(--goat-white)", color: "var(--goat-green)", borderColor: "var(--goat-white)", boxShadow: "4px 4px 0px var(--goat-white)" }}
                data-testid="contact-call-btn"
                aria-label="Call us"
              >
                <Phone size={20} /> {CONTACT.phone}
              </a>
            </div>
          </div>
          <div className="sticker-card p-8" data-testid="contact-info-card">
            <h3 className="text-2xl uppercase mb-3" style={{ color: "var(--goat-green)" }}>Online only, for now</h3>
            <p className="text-base" style={{ color: "var(--goat-green)" }}>
              We're a delivery-first brand. Place your order online and get fresh sliders delivered to your door.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
